import { useQuery } from '@tanstack/react-query';
import { apiFetch, ApiError } from './client.ts';
import { useSession } from '../auth/session.tsx';

/** Whether court report drafts are open to this reader here (api/src/routes/court-reports.ts). */
export interface DraftsAvailable { available: boolean; audited?: boolean }

export function useCourtReportDrafts() {
  const { getToken } = useSession();
  return useQuery({
    queryKey: ['court-reports', 'available'],
    queryFn: () => apiFetch<DraftsAvailable>('/court-reports/available', getToken),
    staleTime: 5 * 60_000,
  });
}

/** Who may sign and which counties, with the defaults a draft takes (api: GET /court-reports/options). */
export interface DraftOptions {
  signer: string | null; county: string;
  staff: { id: string; full_name: string }[];
  counties: { code: string; label: string }[];
}

export function useCourtReportOptions(enabled: boolean) {
  const { getToken } = useSession();
  return useQuery({
    queryKey: ['court-reports', 'options'],
    queryFn: () => apiFetch<DraftOptions>('/court-reports/options', getToken),
    enabled,
  });
}

/** A draft's signer and county, sent only when chosen: both are fixed shapes (a staff id, a catalogue code). */
export interface DraftChoice { signer?: string; county?: string }

function withChoice(path: string, c: DraftChoice): string {
  const q = new URLSearchParams();
  if (c.signer) q.set('signer', c.signer);
  if (c.county) q.set('county', c.county);
  const s = q.toString();
  return s ? `${path}${path.includes('?') ? '&' : '?'}${s}` : path;
}

/** Builds a draft court report and saves it under the name the API gives. */
async function downloadDraft(getToken: () => Promise<string>, path: string, fallback: string): Promise<void> {
  const res = await fetch(`/api${path}`, {
    headers: { authorization: `Bearer ${await getToken()}` },
  });
  if (!res.ok) {
    let message = `The draft could not be generated (${res.status}).`;
    let hint: string | undefined;
    try { const b = (await res.json()) as { error?: string; hint?: string }; message = b.error ?? message; hint = b.hint; } catch { /* not JSON */ }
    throw new ApiError(res.status, message, hint);
  }
  const disposition = res.headers.get('content-disposition') ?? '';
  const name = /filename="([^"]+)"/.exec(disposition)?.[1] ?? fallback;
  const url = URL.createObjectURL(await res.blob());
  const a = document.createElement('a');
  a.href = url; a.download = name; document.body.appendChild(a); a.click(); a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
}

export const downloadAnnualReportDraft = (getToken: () => Promise<string>, clientId: string, year: number, c: DraftChoice = {}) =>
  downloadDraft(getToken, withChoice(`/clients/${clientId}/court-reports/annual/${year}`, c), `Annual Report ${year} DRAFT.docx`);

export const downloadInitialReportDraft = (getToken: () => Promise<string>, clientId: string, c: DraftChoice = {}) =>
  downloadDraft(getToken, withChoice(`/clients/${clientId}/court-reports/initial`, c), 'Initial Report DRAFT.docx');

export const downloadFinalAccountDraft = (getToken: () => Promise<string>, clientId: string, c: DraftChoice = {}) =>
  downloadDraft(getToken, withChoice(`/clients/${clientId}/court-reports/final`, c), 'Final Account DRAFT.docx');
